/**
 * El cliente de caché del panel, en su propio módulo.
 *
 * 🪤 Vivía dentro de `App.tsx`. Los `loader` de las rutas (ADR-0012, paso 2) tienen que poder
 * pedirle datos, y corren FUERA de React: no hay `useQueryClient()` que valga. Importarlo desde
 * `App.tsx` habría creado un ciclo (App → router → App) que Vite no falla al compilar — falla en
 * runtime con un `undefined` a mitad de la inicialización.
 */
import { QueryClient } from '@tanstack/react-query';

/**
 * 🚨 Estaba como `new QueryClient()` — sin configurar. Con los valores por defecto de TanStack Query,
 * `staleTime` es 0: CADA vez que se monta una pantalla los datos se consideran viejos y se vuelven a
 * pedir. O sea que la caché estaba instalada y montada, pero no cacheaba nada entre navegaciones:
 * volver a una lista ya visitada disparaba la misma consulta de cero.
 *
 * Lo reportó el founder: «no cachea la primera búsqueda para que la segunda no se deba cargar de
 * nuevo».
 *
 * 🪤 `staleTime` corto (30 s) y NO largo, a propósito. El riesgo de cachear de más en un panel de
 * operaciones es mostrarle a alguien un pedido que ya cambió — y acá se cobra plata con esos datos.
 * 30 segundos cubre el caso real (ir al detalle y volver, cambiar de pestaña y volver) sin que nadie
 * tome una decisión sobre información vieja.
 *
 * 🪤 `refetchOnWindowFocus` se DEJA en true. Es la red de seguridad del punto anterior: al volver a
 * la pestaña después de un rato, se revalida solo. Apagarlo para «ahorrar consultas» es exactamente
 * cómo se termina viendo un estado que ya no existe.
 *
 * `retry: 1` en vez de 3: con tres reintentos, un error real tarda varios segundos en mostrarse y se
 * siente como lentitud en vez de como una falla.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

// Expone queryClient globalmente para que AuthContext.switchTenant() pueda
// invalidar todo el caché al cambiar de tenant (Multi-tenant V1 — §18).
// En V2 (URL routing), el hard navigation hace este cleanup automático y
// esta exposición se puede eliminar.
if (typeof window !== 'undefined') {
  (window as unknown as { queryClient: QueryClient }).queryClient = queryClient;
}

export { queryClient };
